import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatPoints } from "@/lib/format";
import { Users as UsersIcon, LayoutGrid, TrendingUp, ArrowDownToLine, Crown, ArrowDownCircle, Settings2, Vault, Image as ImageIcon, BadgeCheck, FileText } from "lucide-react";
import AdminUsersTab from "./admin/AdminUsersTab";
import AdminWithdrawalsTab from "./admin/AdminWithdrawalsTab";
import AdminCategoriesTab from "./admin/AdminCategoriesTab";
import AdminMarketsTab from "./admin/AdminMarketsTab";
import AdminAmbassadorsTab from "./admin/AdminAmbassadorsTab";
import AdminDepositsTab from "./admin/AdminDepositsTab";
import AdminWalletConfigTab from "./admin/AdminWalletConfigTab";
import AdminTreasuryTab from "./admin/AdminTreasuryTab";
import AdminHeroTab from "./admin/AdminHeroTab";
import AdminCreatorsTab from "./admin/AdminCreatorsTab";
import AdminContentTab from "./admin/AdminContentTab";

export default function Admin() {
    const { user } = useAuth();
    const [stats, setStats] = useState({});

    const reloadStats = useCallback(async () => {
        try {
            const { data } = await api.get("/stats/overview");
            setStats(data);
        } catch (e) { /* noop */ }
    }, []);

    useEffect(() => {
        if (user && user !== false && user.role === "admin") reloadStats();
    }, [user, reloadStats]);

    if (user === false) return <Navigate to="/login" replace />;
    if (!user) return null;
    if (user.role !== "admin")
        return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-p2w-gray">Acceso denegado</div>;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-sora font-extrabold text-4xl tracking-tighter">Panel de Administración</h1>
                    <p className="mt-2 text-p2w-gray">Gestiona usuarios, tokens, retiros, categorías y mercados.</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    <StatPill label="Usuarios" value={stats.users || 0} />
                    <StatPill label="Mercados" value={stats.markets || 0} />
                    <StatPill label="Bote total" value={formatPoints(stats.total_pool || 0)} accent />
                    <StatPill label="Retiros pend." value={stats.pending_withdrawals || 0} alert={stats.pending_withdrawals > 0} />
                </div>
            </div>

            <Tabs defaultValue="users" className="mt-10">
                <TabsList className="bg-p2w-elevated border border-p2w-line flex-wrap h-auto p-1">
                    <TabsTrigger value="users" data-testid="tab-admin-users" className="data-[state=active]:bg-p2w-green data-[state=active]:text-p2w-green-fg gap-2">
                        <UsersIcon className="w-4 h-4" /> Usuarios
                    </TabsTrigger>
                    <TabsTrigger value="withdrawals" data-testid="tab-admin-withdrawals" className="data-[state=active]:bg-p2w-green data-[state=active]:text-p2w-green-fg gap-2">
                        <ArrowDownToLine className="w-4 h-4" /> Retiros
                    </TabsTrigger>
                    <TabsTrigger value="deposits" data-testid="tab-admin-deposits" className="data-[state=active]:bg-p2w-blue data-[state=active]:text-white gap-2">
                        <ArrowDownCircle className="w-4 h-4" /> Recargas
                    </TabsTrigger>
                    <TabsTrigger value="categories" data-testid="tab-admin-categories" className="data-[state=active]:bg-p2w-green data-[state=active]:text-p2w-green-fg gap-2">
                        <LayoutGrid className="w-4 h-4" /> Categorías
                    </TabsTrigger>
                    <TabsTrigger value="markets" data-testid="tab-admin-markets" className="data-[state=active]:bg-p2w-green data-[state=active]:text-p2w-green-fg gap-2">
                        <TrendingUp className="w-4 h-4" /> Mercados
                    </TabsTrigger>
                    <TabsTrigger value="ambassadors" data-testid="tab-admin-ambassadors" className="data-[state=active]:bg-p2w-purple data-[state=active]:text-white gap-2">
                        <Crown className="w-4 h-4" /> Top Embajadores
                    </TabsTrigger>
                    <TabsTrigger value="wallet-config" data-testid="tab-admin-wallet-config" className="data-[state=active]:bg-p2w-blue data-[state=active]:text-white gap-2">
                        <Settings2 className="w-4 h-4" /> Config Wallet
                    </TabsTrigger>
                    <TabsTrigger value="treasury" data-testid="tab-admin-treasury" className="data-[state=active]:bg-p2w-purple data-[state=active]:text-white gap-2">
                        <Vault className="w-4 h-4" /> Caja de Ganancias
                    </TabsTrigger>
                    <TabsTrigger value="hero" data-testid="tab-admin-hero" className="data-[state=active]:bg-p2w-magenta data-[state=active]:text-white gap-2">
                        <ImageIcon className="w-4 h-4" /> Hero
                    </TabsTrigger>
                    <TabsTrigger value="creators" data-testid="tab-admin-creators" className="data-[state=active]:bg-p2w-purple data-[state=active]:text-white gap-2">
                        <BadgeCheck className="w-4 h-4" /> Creadores
                    </TabsTrigger>
                    <TabsTrigger value="content" data-testid="tab-admin-content" className="data-[state=active]:bg-p2w-magenta data-[state=active]:text-white gap-2">
                        <FileText className="w-4 h-4" /> Contenido
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="users" className="mt-6">
                    <AdminUsersTab onChange={reloadStats} />
                </TabsContent>
                <TabsContent value="withdrawals" className="mt-6">
                    <AdminWithdrawalsTab onChange={reloadStats} />
                </TabsContent>
                <TabsContent value="deposits" className="mt-6">
                    <AdminDepositsTab onChange={reloadStats} />
                </TabsContent>
                <TabsContent value="categories" className="mt-6">
                    <AdminCategoriesTab />
                </TabsContent>
                <TabsContent value="markets" className="mt-6">
                    <AdminMarketsTab onChange={reloadStats} />
                </TabsContent>
                <TabsContent value="ambassadors" className="mt-6">
                    <AdminAmbassadorsTab />
                </TabsContent>
                <TabsContent value="wallet-config" className="mt-6">
                    <AdminWalletConfigTab />
                </TabsContent>
                <TabsContent value="treasury" className="mt-6">
                    <AdminTreasuryTab />
                </TabsContent>
                <TabsContent value="hero" className="mt-6">
                    <AdminHeroTab />
                </TabsContent>
                <TabsContent value="creators" className="mt-6">
                    <AdminCreatorsTab />
                </TabsContent>
                <TabsContent value="content" className="mt-6">
                    <AdminContentTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function StatPill({ label, value, accent, alert }) {
    return (
        <div className={`px-4 py-2.5 rounded-xl border ${alert ? "border-p2w-magenta/60 bg-p2w-magenta/10" : "border-p2w-line bg-p2w-surface"}`}>
            <div className="text-[10px] uppercase tracking-widest text-p2w-gray">{label}</div>
            <div className={`mt-0.5 font-sora font-bold text-xl number-tabular ${accent ? "text-p2w-green" : alert ? "text-p2w-magenta" : "text-white"}`}>
                {value}
            </div>
        </div>
    );
}
